import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/outline";
import {
  addLocalNotification,
  clearPeriodResult,
  requestClosePeriod,
  requestReopenPeriod,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTransactionState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import {
  formatPeriodDate,
  formatPeriodDateTime,
  formatPeriodMonth,
  toPeriodThrough,
} from "../../../../Utils/PeriodUtils";
import { ModalAlert } from "../../../ModalAlert";
import { ModalConfirm } from "../../../ModalConfirm";
import Button from "../../Button";
import FormDate from "../../Form/FormDate";
import FormInput from "../../Form/FormInput";
import FormRadioGroup from "../../Form/FormRadioGroup";
import Pagination from "../../Pagination";
import Table from "../../Table";
import FormError from "../../Form/FormError";

const MONTH_END = "MONTH_END";
const FISCAL_YEAR_END = "FISCAL_YEAR_END";
const REOPEN_ALL = "REOPEN_ALL";
const REOPEN_THROUGH = "REOPEN_THROUGH";

/**
 * PeriodClosePanel — the whole Tutup Buku surface: current status, close, reopen,
 * and the audit history.
 *
 * Owns no page chrome at all, which is what lets one implementation serve both
 * routes: the v2 page wraps it in AccountingWrapper + AccountingPageHeader, while
 * the v1 page renders it bare and gets its heading from App.js via the route
 * title. Built only from primitives both layouts already use.
 */
const PeriodClosePanel = ({ businessID, business }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const { businesses } = useBusinessTransactionState();
  const {
    closePeriodLoading,
    closePeriodError,
    closePeriodResult,
    reopenPeriodLoading,
    reopenPeriodError,
    reopenPeriodResult,
  } = useAccountingState();

  const {
    status,
    closedThrough,
    closedThroughLabel,
    history,
    loading,
    error,
    refetch,
  } = usePeriodLock(businessID, { locale: i18n.language });

  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage, setHistoryPerPage] = useState(5);

  const [closeReason, setCloseReason] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [reopenDate, setReopenDate] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [showRestricted, setShowRestricted] = useState(false);

  // Closing and reopening are both writes, so a RESTRICTED (or free-limit
  // PENDING_DELETION) business is barred from either — the backend guard would
  // reject them anyway. Resolved from whatever the caller knows: the v2 page
  // passes mainBusiness directly, v1 arrives with a ?id= that has to be looked
  // up. An unloaded list falls through to "not blocked", which fails in the
  // safe direction: the click is allowed and the server has the final say,
  // rather than a healthy business being locked out by a race.
  const targetBusiness =
    business ||
    (user?.mainBusiness?.id === businessID ? user.mainBusiness : null) ||
    (businesses || []).find((item) => item.id === businessID) ||
    null;

  const writeBlocked = isBusinessWriteBlocked(targetBusiness);

  const closeOptions = useMemo(() => {
    const options = [];
    if (status?.nextMonthEndCandidate) {
      options.push({
        id: MONTH_END,
        value: status.nextMonthEndCandidate,
        label: t("accounting.period.fields.throughMonth", {
          month: formatPeriodMonth(status.nextMonthEndCandidate, i18n.language),
        }),
      });
    }
    if (status?.nextFiscalYearEndCandidate) {
      options.push({
        id: FISCAL_YEAR_END,
        value: status.nextFiscalYearEndCandidate,
        // The year comes from the API, never computed here — that is what keeps
        // a non-January fiscalYearStartMonth labelled correctly.
        fiscalYear: status.nextFiscalYearEndCandidateYear,
        label: t("accounting.period.fields.throughFiscalYear", {
          year: status.nextFiscalYearEndCandidateYear,
        }),
      });
    }
    return options;
  }, [status, t, i18n.language]);

  // Side by side only when the close card has enough in it to hold up its half
  // of the row — that is the fiscal-year-end month, where a month-end and a
  // year-end candidate stack up to roughly the reopen card's height. In an
  // ordinary month there is one candidate (or none, before the month ends), and
  // pairing leaves most of the left column blank; stacked, both cards keep the
  // full width and neither looks unfinished.
  const paired = Boolean(closedThrough) && closeOptions.length > 1;

  const [closeTarget, setCloseTarget] = useState(null);

  // Default to the nearest candidate, and re-seed when the candidates change
  // (i.e. right after a successful close).
  useEffect(() => {
    setCloseTarget((current) => {
      if (current && closeOptions.some((option) => option.id === current.id)) {
        return closeOptions.find((option) => option.id === current.id);
      }
      return closeOptions[0] || null;
    });
  }, [closeOptions]);

  const reopenOptions = useMemo(
    () => [
      { id: REOPEN_ALL, label: t("accounting.period.fields.reopenAll") },
      { id: REOPEN_THROUGH, label: t("accounting.period.fields.reopenThrough") },
    ],
    [t],
  );
  const [reopenScope, setReopenScope] = useState(reopenOptions[0]);

  // A reopen may only move backwards, so the current boundary is the ceiling.
  const reopenMaxDate = useMemo(() => {
    if (!closedThrough) return undefined;
    const iso = toPeriodThrough(closedThrough);
    if (!iso) return undefined;
    const [year, month, day] = iso.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [closedThrough]);

  useEffect(() => {
    if (closePeriodResult) {
      setShowCloseConfirm(false);
      setCloseReason("");
      addLocalNotification(notificationDispatch, {
        id: `period-close-${closePeriodResult.closedThrough}`,
        type: "success",
        title: "common.success",
        body: t("accounting.period.notifications.closeSuccess", {
          date: formatPeriodDate(closePeriodResult.closedThrough, i18n.language),
        }),
        showFull: true,
      });
      clearPeriodResult(dispatch);
      // The write response carries the new boundary but not the new history row.
      refetch();
      // History is newest-first, so the row just written lands on page 1 — where
      // the user should be looking after acting.
      setHistoryPage(1);
    }
  }, [closePeriodResult, dispatch, notificationDispatch, refetch, t, i18n.language]);

  useEffect(() => {
    if (reopenPeriodResult) {
      setShowReopenConfirm(false);
      setReopenReason("");
      setReopenDate(null);
      addLocalNotification(notificationDispatch, {
        id: `period-reopen-${Date.now()}`,
        type: "success",
        title: "common.success",
        body: t("accounting.period.notifications.reopenSuccess"),
        showFull: true,
      });
      clearPeriodResult(dispatch);
      refetch();
      setHistoryPage(1);
    }
  }, [reopenPeriodResult, dispatch, notificationDispatch, refetch, t]);

  // Close the confirmation dialogs on rejection so the inline notice is visible.
  useEffect(() => {
    if (closePeriodError) setShowCloseConfirm(false);
    if (reopenPeriodError) setShowReopenConfirm(false);
  }, [closePeriodError, reopenPeriodError]);

  useEffect(() => () => clearPeriodResult(dispatch), [dispatch]);

  // The triggers stay enabled on a blocked business and explain themselves on
  // click — a silently disabled button teaches nothing, and this is the one
  // reason the user can actually act on. Mirrors the premium prompt on
  // Pages/NPWPValidate.
  const onCloseClick = () => {
    if (writeBlocked) {
      setShowRestricted(true);
      return;
    }
    setShowCloseConfirm(true);
  };

  const onReopenClick = () => {
    if (writeBlocked) {
      setShowRestricted(true);
      return;
    }
    setShowReopenConfirm(true);
  };

  const onConfirmClose = () => {
    if (!closeTarget) return;
    requestClosePeriod(dispatch, {
      businessID,
      // Exactly one of these reaches the body — the fiscal-year option is sent
      // as a year so the server does its own fiscal-calendar snapping.
      fiscalYear: closeTarget.id === FISCAL_YEAR_END ? closeTarget.fiscalYear : undefined,
      through: closeTarget.id === MONTH_END ? toPeriodThrough(closeTarget.value) : undefined,
      reason: closeReason.trim() || undefined,
    });
  };

  const onConfirmReopen = () => {
    requestReopenPeriod(dispatch, {
      businessID,
      through:
        reopenScope?.id === REOPEN_THROUGH ? toPeriodThrough(reopenDate) : undefined,
      reason: reopenReason.trim(),
    });
  };

  const reopenReady =
    Boolean(reopenReason.trim()) &&
    (reopenScope?.id !== REOPEN_THROUGH || Boolean(reopenDate));

  const closeTargetLabel = formatPeriodDate(closeTarget?.value, i18n.language);

  // The whole history arrives in the status response, so paging is a slice —
  // same client-side arrangement the fixed-asset and trial-balance tables use.
  // The page is clamped on read rather than corrected in an effect: switching to
  // a business with a shorter history would otherwise render an empty table for
  // a frame before the effect pulled the page back in range.
  const historyTotalPages = Math.max(
    1,
    Math.ceil(history.length / historyPerPage),
  );
  const currentHistoryPage = Math.min(historyPage, historyTotalPages);
  const pagedHistory = history.slice(
    (currentHistoryPage - 1) * historyPerPage,
    currentHistoryPage * historyPerPage,
  );

  const historyColumns = useMemo(
    () => [
      {
        header: "accounting.period.history.field.date",
        accessor: (row) => formatPeriodDateTime(row.createdAt, i18n.language),
      },
      {
        header: "accounting.period.history.field.event",
        accessor: (row) => (
          <span
            className={
              row.event === "CLOSED"
                ? "inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"
                : "inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/20"
            }
          >
            {row.event === "CLOSED"
              ? t("accounting.period.history.event.closed")
              : t("accounting.period.history.event.reopened")}
          </span>
        ),
      },
      {
        header: "accounting.period.history.field.through",
        accessor: (row) => (
          <div>
            {/* An em dash, not "null": a full reopen genuinely has no boundary. */}
            <div className="text-gray-900">
              {row.closedThrough
                ? formatPeriodDate(row.closedThrough, i18n.language)
                : "—"}
            </div>
            {row.previousClosedThrough && (
              <div className="text-xs text-gray-400">
                {t("accounting.period.history.field.previous", {
                  date: formatPeriodDate(row.previousClosedThrough, i18n.language),
                })}
              </div>
            )}
          </div>
        ),
      },
      {
        header: "accounting.period.history.field.reason",
        accessor: (row) => (
          <span className={row.reason ? "text-gray-700" : "text-gray-400"}>
            {row.reason || "—"}
          </span>
        ),
      },
    ],
    [t, i18n.language],
  );

  return (
    <div className="space-y-5">
      {/* Status ------------------------------------------------------------ */}
      {/* Three states, not two. "Buku belum pernah ditutup" is a definite claim
          about the ledger — that every journal is still editable — so it must
          not be the stand-in for "the status has not arrived". Before this
          branch existed the card asserted it on every cold load, and kept
          asserting it for a business whose status failed to load at all. */}
      {!status && loading ? (
        <div className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="mt-0.5 h-5 w-5 flex-shrink-0 animate-pulse rounded bg-gray-200" />
          <div className="w-full">
            <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-3 w-72 max-w-full animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      ) : !status ? null : closedThrough ? (
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4">
          <LockClosedIcon
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {t("accounting.period.status.closedThrough", {
                date: closedThroughLabel,
              })}
            </p>
            {Boolean(status?.currentFiscalYear) && (
              <p className="mt-0.5 text-xs text-amber-700">
                {t("accounting.period.status.fiscalYear", {
                  year: status.currentFiscalYear,
                })}
              </p>
            )}
            {/* A label/value pair rather than a sentence: "Revisi buku 3" reads
                as one dangling phrase, where dt/dd scans as the metadata it is.
                Hidden at 0 rather than at null: the backend always sends a
                number, so `!= null` printed "Revisi buku · 0" on every business
                that had never been reopened — a count worth reading only once
                there is something to count. */}
            {status?.ledgerRevision > 0 && (
              <dl className="mt-0.5 flex items-baseline gap-1 text-xs text-amber-700">
                <dt className="after:ml-1 after:text-amber-600 after:content-['·']">
                  {t("accounting.period.status.revision")}
                </dt>
                <dd className="font-medium">{status.ledgerRevision}</dd>
              </dl>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-4">
          <LockOpenIcon
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {t("accounting.period.status.neverClosed")}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("accounting.period.status.neverClosedHint")}
            </p>
            {/* Reachable with a non-zero count: this branch is "not currently
                closed", which includes a book that was closed and then fully
                reopened. */}
            {status?.ledgerRevision > 0 && (
              <dl className="mt-0.5 flex items-baseline gap-1 text-xs text-gray-500">
                <dt className="after:ml-1 after:text-gray-400 after:content-['·']">
                  {t("accounting.period.status.revision")}
                </dt>
                <dd className="font-medium">{status.ledgerRevision}</dd>
              </dl>
            )}
          </div>
        </div>
      )}

      <FormError error={error} onRetry={refetch} />

      {/* Close + reopen are two halves of one decision, so they sit side by side
          once there is room — stacked, the reopen card fell below the fold on
          exactly the books that need it. `paired` is what decides; see its
          definition for when a pair is worth making. When they do pair they
          stretch to a shared height (the grid default) rather than each taking
          its natural one, because two boxes ending at different heights read as
          a rendering slip. Each card is a flex column so the height they gain
          lands in one place — the gap above the action row — keeping both
          buttons on the same line. */}
      <div className={`grid gap-5 ${paired ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {/* Close ----------------------------------------------------------- */}
        <section className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("accounting.period.actions.close")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("accounting.period.hints.close")}
          </p>

          {/* The API's own rejection message is shown verbatim — it already
              names the recovery step, and a closed period is a status, not an
              error. */}
          <FormError className="mt-4" error={closePeriodError} />

          {closeOptions.length === 0 ? (
            // Without a status there is no telling what can close; the load
            // error above says so instead of "nothing to close".
            status && (
              <p className="mt-4 text-sm text-gray-500">
                {t("accounting.period.hints.noCandidate")}
              </p>
            )
          ) : (
            <div className="mt-4 flex flex-1 flex-col">
              <div className="space-y-4">
                <FormRadioGroup
                  name="period-close-target"
                  options={closeOptions}
                  value={closeTarget}
                  onChange={setCloseTarget}
                  // One column while the cards are paired: a half-width card
                  // puts two candidates at ~200px each, where "Sampai akhir
                  // Desember 2025" wraps to two lines. Full width keeps the
                  // pair side by side.
                  gridClassName={
                    paired
                      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-1"
                      : "grid-cols-1 sm:grid-cols-2"
                  }
                />
                <FormInput
                  name="period-close-reason"
                  label="accounting.period.fields.reason"
                  hint={t("common.optional")}
                  value={closeReason}
                  onChange={(event) => setCloseReason(event.target.value)}
                  placeholder={t("accounting.period.placeholders.closeReason")}
                />
              </div>
              {/* Outside the space-y stack, not inside it: `mt-auto` has to win
                  over the stack's own margin rule to pin the row to the bottom,
                  and as a sibling of the stack it simply does. */}
              <div className="mt-auto flex justify-end pt-4">
                <Button
                  onClick={onCloseClick}
                  // Stays clickable while blocked so the explanation is
                  // reachable without first filling in a form the user cannot
                  // submit.
                  disabled={
                    (!writeBlocked && !closeTarget) || closePeriodLoading
                  }
                  loading={closePeriodLoading}
                  className="active:scale-[0.96]"
                  style={{ transitionProperty: "opacity, transform" }}
                >
                  {t("accounting.period.actions.close")}
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Reopen ---------------------------------------------------------- */}
        {Boolean(closedThrough) && (
          <section className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-gray-900">
              {t("accounting.period.actions.reopen")}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("accounting.period.hints.reopen")}
            </p>

            <FormError className="mt-4" error={reopenPeriodError} />

            <div className="mt-4 flex flex-1 flex-col">
              <div className="space-y-4">
                <FormRadioGroup
                  name="period-reopen-scope"
                  options={reopenOptions}
                  value={reopenScope}
                  onChange={setReopenScope}
                  gridClassName={
                    paired
                      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-1"
                      : "grid-cols-1 sm:grid-cols-2"
                  }
                />

                {reopenScope?.id === REOPEN_THROUGH && (
                  <FormDate
                    name="period-reopen-through"
                    label="accounting.period.fields.reopenThroughDate"
                    selected={reopenDate}
                    onChange={setReopenDate}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t("Select Date")}
                    maxDate={reopenMaxDate}
                    onClear={() => setReopenDate(null)}
                  />
                )}

                {/* Required, and the requirement is stated rather than merely
                    enforced — the reason is what makes this control
                    auditable. */}
                <FormInput
                  name="period-reopen-reason"
                  label="accounting.period.fields.reason"
                  required
                  value={reopenReason}
                  onChange={(event) => setReopenReason(event.target.value)}
                  placeholder={t("accounting.period.placeholders.reopenReason")}
                />
                <p className="text-xs text-gray-500">
                  {t("accounting.period.hints.reasonSaved")}
                </p>
              </div>

              <div className="mt-auto flex justify-end pt-4">
                <Button
                  variant="secondary"
                  onClick={onReopenClick}
                  disabled={
                    (!writeBlocked && !reopenReady) || reopenPeriodLoading
                  }
                  loading={reopenPeriodLoading}
                  className="active:scale-[0.96]"
                  style={{ transitionProperty: "opacity, transform" }}
                >
                  {t("accounting.period.actions.reopen")}
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* History ----------------------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">
          {t("accounting.period.history.title")}
        </h2>
        <Table
          columns={historyColumns}
          data={pagedHistory}
          isLoading={loading && history.length === 0}
          // Without the error a failed load read as "no history yet".
          error={history.length === 0 ? error : null}
          onRetry={refetch}
        />
        {/* Shown for any non-empty history, not only once it overflows one
            page: the per-page select lives here, so hiding the bar at seven
            rows and a page size of 50 would leave no way back to 5. The empty
            and error states keep it away, where there is nothing to page. */}
        {history.length > 0 && (
          <Pagination
            isOffline
            items={history}
            currentPage={currentHistoryPage}
            isLoading={loading}
            perPage={historyPerPage}
            onPageChange={(nextPage) => setHistoryPage(nextPage)}
            onPerPageChange={(nextPerPage) => {
              setHistoryPerPage(nextPerPage);
              setHistoryPage(1);
            }}
          />
        )}
      </section>

      <ModalAlert
        open={showCloseConfirm}
        setOpen={setShowCloseConfirm}
        type="warning"
        title={t("accounting.period.modal.closeTitle", { date: closeTargetLabel })}
        description={t("accounting.period.modal.closeDescription", {
          date: closeTargetLabel,
        })}
        confirmButtonText="accounting.period.actions.close"
        cancelButtonText="common.cancel"
        onConfirm={onConfirmClose}
        onCancel={() => setShowCloseConfirm(false)}
        loadingButton={closePeriodLoading}
      />

      <ModalAlert
        open={showReopenConfirm}
        setOpen={setShowReopenConfirm}
        type="warning"
        title="accounting.period.modal.reopenTitle"
        description={t("accounting.period.modal.reopenDescription")}
        confirmButtonText="accounting.period.actions.reopen"
        cancelButtonText="common.cancel"
        onConfirm={onConfirmReopen}
        onCancel={() => setShowReopenConfirm(false)}
        loadingButton={reopenPeriodLoading}
        disabledButton={!reopenReady}
      />

      {/* Restricted / scheduled-for-deletion businesses cannot write at all, so
          closing the books is off the table until the subscription is restored.
          Same premium prompt shape as Pages/NPWPValidate. */}
      <ModalConfirm
        open={showRestricted}
        setOpen={setShowRestricted}
        type="WARNING"
        title={t("accounting.period.modal.restrictedTitle")}
        description={t("accounting.period.modal.restrictedDescription")}
        confirmTitle={t("Subscribe")}
        onConfirm={() => navigate("/subscription")}
      />
    </div>
  );
};

export default PeriodClosePanel;
